import React, { useState, useRef, useEffect } from 'react';
import { motion, PanInfo, useAnimation } from 'framer-motion';
import {
  Users, BarChart3, Building2, TrendingUp, ArrowRight, Activity,
  GitBranch, RefreshCw, Scale, FileText, CheckCircle, Target,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { camaraAPI } from '../../services/api';
import { usePeriod } from '../../contexts/PeriodContext';

interface CardData {
  title: string;
  description: string;
  icon: React.FC<{ className?: string }>;
  color: string;
  bgGradient: string;
  path: string;
  stats: {
    label: string;
    value: string | number;
    trend?: 'up' | 'down' | 'neutral';
  };
  liveData?: boolean;
}

export const QuickAccessCardsMobile: React.FC = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const controls = useAnimation();
  const constraintsRef = useRef<HTMLDivElement>(null);

  // Add haptic feedback for mobile devices
  const triggerHapticFeedback = (type: 'light' | 'medium' | 'heavy' = 'light') => {
    if ('vibrate' in navigator) {
      const patterns = {
        light: 10,
        medium: 20,
        heavy: 30,
      };
      navigator.vibrate(patterns[type]);
    }
  };

  const [cards, setCards] = useState<CardData[]>([
    {
      title: '¿Faltó a una ley importante?',
      description: 'Descubre la asistencia de cada diputado en votaciones clave',
      icon: Users,
      color: 'blue',
      bgGradient: 'from-blue-500 to-blue-600',
      path: '/diputados',
      stats: {
        label: 'Asistencia promedio',
        value: '…',
        trend: 'neutral',
      },
      liveData: true,
    },
    {
      title: '¿Cómo votó mi diputado?',
      description: 'Revisa su posición en cada proyecto de ley',
      icon: BarChart3,
      color: 'purple',
      bgGradient: 'from-purple-500 to-purple-600',
      path: '/votaciones',
      stats: {
        label: 'Votaciones del periodo',
        value: '…',
        trend: 'neutral',
      },
      liveData: true,
    },
    {
      title: '¿Cómo vota cada partido?',
      description: 'Analiza el comportamiento de los bloques políticos',
      icon: Building2,
      color: 'green',
      bgGradient: 'from-green-500 to-green-600',
      path: '/partidos',
      stats: {
        label: 'Partidos activos',
        value: '…',
        trend: 'neutral',
      },
      liveData: true,
    },
    {
      title: '¿Qué cosas se legislan?',
      description: 'Explora los proyectos de ley en discusión',
      icon: TrendingUp,
      color: 'orange',
      bgGradient: 'from-orange-500 to-orange-600',
      path: '/votaciones',
      stats: {
        label: 'Proyectos votados',
        value: '…',
        trend: 'neutral',
      },
      liveData: true,
    },
  ]);

  // Every card shows a real number from /api/stats for the selected period.
  const { selectedPeriod } = usePeriod();
  useEffect(() => {
    let cancelled = false;
    camaraAPI.getSummaryStats(selectedPeriod === 'all' ? undefined : selectedPeriod)
      .then((stats: any) => {
        if (cancelled) return;
        const live: Record<string, string | number> = {
          'Asistencia promedio': stats.average_attendance != null ? `${Number(stats.average_attendance).toFixed(1)}%` : '—',
          'Votaciones del periodo': stats.total_votes ?? '—',
          'Partidos activos': stats.party_count ?? '—',
          'Proyectos votados': stats.total_boletins ?? '—',
        };
        setCards(prev => prev.map(card =>
          card.liveData && live[card.stats.label] !== undefined
            ? { ...card, stats: { ...card.stats, value: live[card.stats.label] } }
            : card
        ));
      })
      .catch(() => { /* keep the placeholders */ });
    return () => { cancelled = true; };
  }, [selectedPeriod]);

  const handleCardPress = (path: string) => {
    triggerHapticFeedback('medium');
    navigate(path);
  };

  const handleSwipe = (info: PanInfo) => {
    const threshold = 50;
    const velocity = 500;

    if (Math.abs(info.offset.x) > threshold || Math.abs(info.velocity.x) > velocity) {
      triggerHapticFeedback('light');

      if (info.offset.x > 0) {
        // Swipe right - previous card
        setCurrentIndex(prev => prev > 0 ? prev - 1 : cards.length - 1);
      } else {
        // Swipe left - next card
        setCurrentIndex(prev => prev < cards.length - 1 ? prev + 1 : 0);
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 25,
      },
    },
  };

  return (
    <section className="py-8 px-4 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header - Mobile Optimized */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
            Explora el Congreso
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            ¡Tu voto importa! Información clave sobre el trabajo parlamentario
          </p>
        </motion.div>

        {/* Mobile-First Layout */}
        <div className="block lg:hidden">
          {/* Mobile: Single Card View with Swipe */}
          <div className="relative">
            {/* Card Indicators */}
            <div className="flex justify-center space-x-2 mb-6">
              {cards.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentIndex(index);
                    triggerHapticFeedback('light');
                  }}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-blue-600 w-8'
                      : 'bg-gray-300'
                  }`}
                  style={{ minWidth: '20px', minHeight: '20px', padding: '9px' }}
                  aria-label={`Go to card ${index + 1}`}
                />
              ))}
            </div>

            {/* Swipeable Card */}
            <motion.div
              className="px-4"
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.1}
              onDragEnd={(_, info) => handleSwipe(info)}
              whileTap={{ scale: 0.98 }}
            >
              <MobileCard
                card={cards[currentIndex]}
                onPress={handleCardPress}
                index={currentIndex}
              />
            </motion.div>

            {/* Swipe Hint */}
            <div className="text-center mt-4">
              <p className="text-xs text-gray-500">
                Desliza para ver más opciones
              </p>
            </div>
          </div>
        </div>

        {/* Desktop/Tablet: Grid Layout */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="hidden lg:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {cards.map((card, index) => (
            <DesktopCard
              key={index}
              card={card}
              onPress={handleCardPress}
              index={index}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Mobile-optimized card component
const MobileCard: React.FC<{
  card: CardData;
  onPress: (path: string) => void;
  index: number;
}> = ({ card, onPress, index }) => {
  const Icon = card.icon;

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, scale: 0.9 },
        visible: {
          opacity: 1,
          scale: 1,
          transition: {
            type: 'spring',
            stiffness: 400,
            damping: 20,
            delay: index * 0.1,
          },
        },
      }}
      whileTap={{ scale: 0.95 }}
      onClick={() => onPress(card.path)}
      className="relative group cursor-pointer"
      style={{
        minHeight: '280px',
        minWidth: '100%',
      }}
    >
      {/* Glow effect */}
      <div
        className={`
          absolute -inset-1 bg-gradient-to-r ${card.bgGradient}
          rounded-2xl opacity-0 group-active:opacity-30 blur-lg
          transition-opacity duration-200
        `}
      />

      {/* Card Content */}
      <div className="relative bg-white rounded-2xl shadow-lg group-active:shadow-xl transition-all duration-200 overflow-hidden h-full">
        {/* Top gradient accent */}
        <div className={`h-2 bg-gradient-to-r ${card.bgGradient}`} />

        <div className="p-6 flex flex-col h-full">
          {/* Icon and Stats Row */}
          <div className="flex items-start justify-between mb-6">
            <div className={`inline-flex p-4 rounded-xl bg-gradient-to-r ${card.bgGradient} shadow-lg`}>
              <Icon className="w-8 h-8 text-white" />
            </div>

            {/* Stats */}
            <div className="text-right">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                {card.stats.label}
              </p>
              <div className="flex items-center gap-2 justify-end">
                <span className="text-xl font-bold text-gray-900">
                  {card.stats.value}
                </span>
                {card.stats.trend && (
                  <span className={`
                    text-xs font-medium px-2 py-1 rounded-full
                    ${card.stats.trend === 'up'
                      ? 'bg-green-100 text-green-700'
                      : card.stats.trend === 'down'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-700'
                    }
                  `}>
                    {card.stats.trend === 'up' ? '↑' : card.stats.trend === 'down' ? '↓' : '→'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-gray-900 mb-3 group-active:text-blue-600 transition-colors">
            {card.title}
          </h3>

          {/* Description */}
          <p className="text-gray-600 mb-6 flex-grow leading-relaxed">
            {card.description}
          </p>

          {/* Action Button */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <span className="text-sm font-medium text-gray-700">
              Explorar ahora
            </span>
            <div
              className={`
                w-12 h-12 rounded-full bg-gradient-to-r ${card.bgGradient}
                flex items-center justify-center shadow-lg
                group-active:scale-95 transition-transform duration-200
              `}
              style={{ minWidth: '48px', minHeight: '48px' }}
            >
              <ArrowRight className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Desktop card component (optimized)
const DesktopCard: React.FC<{
  card: CardData;
  onPress: (path: string) => void;
  index: number;
}> = ({ card, onPress, index }) => {
  const Icon = card.icon;

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            type: 'spring',
            stiffness: 400,
            damping: 25,
            delay: index * 0.1,
          },
        },
      }}
      whileHover={{ y: -8, transition: { duration: 0.2 } }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onPress(card.path)}
      className="relative group cursor-pointer"
      style={{ minHeight: '320px' }}
    >
      {/* Glow effect on hover */}
      <div
        className={`
          absolute -inset-0.5 bg-gradient-to-r ${card.bgGradient}
          rounded-xl opacity-0 group-hover:opacity-75 blur-lg
          transition-opacity duration-300
        `}
      />

      {/* Card Content */}
      <div className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden h-full">
        {/* Top gradient accent */}
        <div className={`h-1 bg-gradient-to-r ${card.bgGradient}`} />

        <div className="p-6 flex flex-col h-full">
          {/* Icon */}
          <div className="flex items-start justify-between mb-4">
            <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${card.bgGradient} shadow-lg`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
            {card.title}
          </h3>

          {/* Description */}
          <p className="text-gray-600 text-sm mb-4 line-clamp-2 flex-grow">
            {card.description}
          </p>

          {/* Stats Section */}
          <div className="pt-4 border-t border-gray-100 mt-auto">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  {card.stats.label}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-2xl font-bold text-gray-900">
                    {card.stats.value}
                  </p>
                  {card.stats.trend && (
                    <span className={`
                      text-xs font-medium px-1.5 py-0.5 rounded-full
                      ${card.stats.trend === 'up'
                        ? 'bg-green-100 text-green-700'
                        : card.stats.trend === 'down'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-700'
                      }
                    `}>
                      {card.stats.trend === 'up' ? '↑' : card.stats.trend === 'down' ? '↓' : '→'}
                    </span>
                  )}
                </div>
              </div>

              {/* Arrow Icon */}
              <div
                className={`
                  w-10 h-10 rounded-full bg-gray-100 
                  flex items-center justify-center
                  group-hover:bg-gradient-to-r group-hover:${card.bgGradient}
                  transition-all duration-300
                `}
                style={{ minWidth: '44px', minHeight: '44px' }}
              >
                <ArrowRight className={`
                  w-5 h-5 text-gray-600 
                  group-hover:text-white group-hover:translate-x-1
                  transition-all duration-300
                `} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};